import { useEffect, useState } from "react";
import { apiUrl } from "../utils/api";
import { BEARER_TOKEN_KEY } from "../utils/auth-client";

export type BlobUrlState = {
  /** Object-URL (`blob:`) once loaded, otherwise null. */
  url: string | null;
  loading: boolean;
  error: boolean;
};

// Geteilter Blob-Cache je Ref (Profilfotos `/users/:id/photo`, `/media/…`): dedupt
// gleichzeitige UND wiederholte Fetches — N Avatare desselben Users teilen EINEN
// Netz-Request (vorher: ein Fetch pro Avatar-Instanz, live bis 17× dasselbe Foto).
// Konsumenten bauen ihre EIGENE Object-URL aus dem geteilten Blob, damit das
// Revoke beim Unmount niemanden anderen trifft. Fehlversuche werden verworfen,
// damit ein späterer Mount neu laden kann.
//
// Exportiert, weil die Notizblöcke (`notizbloecke/bildDarstellung.ts`) ihre
// Bilder IMPERATIV in ein contentEditable hydratisieren — ein Hook kommt dort
// nicht in Frage, und derselbe Cache spart den zweiten Request, wenn dasselbe
// Bild in Strom und Kundentagebuch steht.
const BLOB_CACHE = new Map<string, Promise<Blob>>();
const BLOB_CACHE_MAX = 64;
let blobCacheAuthContext = "";

/** Entfernt alle geschützten Bytes. Object-URLs liegen nie in diesem Cache. */
export function clearBlobCache(): void {
  BLOB_CACHE.clear();
}

/**
 * Bindet den Cache an Mandant und Nutzer. Ein Sessionwechsel leert die alten
 * Bytes sofort; der Token bleibt zusätzlich Teil jedes einzelnen Cache-Keys.
 */
export function setBlobCacheAuthContext(authContext: string | null | undefined): void {
  const next = String(authContext ?? "");
  if (next === blobCacheAuthContext) return;
  clearBlobCache();
  blobCacheAuthContext = next;
}

const aktuellerToken = (): string => {
  try {
    return localStorage.getItem(BEARER_TOKEN_KEY) ?? "";
  } catch {
    return "";
  }
};

const cacheKey = (ref: string): string => `${blobCacheAuthContext}\u0000${aktuellerToken()}\u0000${ref}`;

export function fetchBlobCached(ref: string): Promise<Blob> {
  const key = cacheKey(ref);
  const existing = BLOB_CACHE.get(key);
  if (existing) {
    // Map-Reihenfolge als leichtgewichtige LRU-Liste verwenden.
    BLOB_CACHE.delete(key);
    BLOB_CACHE.set(key, existing);
    return existing;
  }
  // apiUrl hängt `/api` an; der fetch-Wrapper ergänzt den Bearer-Token.
  const p = fetch(apiUrl(ref.replace(/^\/+/, ""))).then((res) =>
    res.ok ? res.blob() : Promise.reject(res.status),
  );
  while (BLOB_CACHE.size >= BLOB_CACHE_MAX) {
    const oldest = BLOB_CACHE.keys().next().value as string | undefined;
    if (oldest === undefined) break;
    // Der Cache enthält Blobs, keine Object-URLs. Es gibt hier bewusst nichts
    // zu revoke; jede Hook-/Notiz-Instanz widerruft ihre eigene URL beim Ende.
    BLOB_CACHE.delete(oldest);
  }
  BLOB_CACHE.set(key, p);
  p.catch(() => {
    if (BLOB_CACHE.get(key) === p) BLOB_CACHE.delete(key);
  });
  return p;
}

/**
 * Lädt eine geschützte API-Ressource (z. B. `/media/...`) authentifiziert als
 * Blob und stellt sie als `blob:`-Object-URL bereit — gedacht für `<img>`/`<a>`,
 * die keinen Authorization-Header setzen können.
 *
 * Warum: Ein nacktes `<img src="/api/media/...">` hängt heute am Session-Cookie
 * (bzw. `access_token`-Query). Sobald der Backend-Guard auf **Bearer-only**
 * umgestellt ist, bekäme so ein Subrequest 401. Der globale fetch-Wrapper in
 * `utils/api.ts` hängt den `Authorization: Bearer`-Header an jede API-URL — also
 * holen wir die Bytes per `fetch(apiUrl(ref))`, machen daraus eine `blob:`-URL
 * und geben sie beim Unmount/Wechsel wieder frei (`revokeObjectURL`).
 *
 * @param ref API-relativer Pfad (z. B. `"/media/news/foo.jpg"` oder
 *            `"media/contacts/bar.png"`). Absolute `http(s)`-URLs werden
 *            unverändert durchgereicht (kein Fetch, kein Bearer). `null`/leer →
 *            kein Laden.
 */
export function useBlobUrl(ref: string | null | undefined): BlobUrlState {
  const [state, setState] = useState<BlobUrlState>({
    url: null,
    loading: false,
    error: false,
  });

  useEffect(() => {
    if (!ref) {
      setState({ url: null, loading: false, error: false });
      return;
    }

    // Externe absolute URLs nicht durch den authentifizierten Fetch schleusen.
    if (/^https?:\/\//i.test(ref)) {
      setState({ url: ref, loading: false, error: false });
      return;
    }

    let objectUrl: string | null = null;
    let cancelled = false;
    setState({ url: null, loading: true, error: false });

    // Geteilter Cache: genau 1 Netz-Fetch je Ref; jede Instanz eigene Object-URL.
    fetchBlobCached(ref)
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setState({ url: objectUrl, loading: false, error: false });
      })
      .catch(() => {
        if (!cancelled) setState({ url: null, loading: false, error: true });
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [ref]);

  return state;
}
